// apps/web/src/features/clients/components/clientRowParts.tsx
//
// Row-level building blocks shared by the desktop table and the mobile cards,
// so both layouts show the same facts and honour the same activation rules.
//
// INVARIANT (AC-3/AC-4): the client name is a REAL `<Link>`, so Ctrl/Cmd/middle
// click and "open in new tab" work like anywhere else in the browser — and it
// stops propagation, so a modified click never ALSO drives the parent row/card
// activation into the current tab.
import type { CSSProperties, ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { formatPhone, phoneSchema, type ClientListItem } from '@magimp/shared';
import { ContactActions, Icon, IconButton, StatusBadge } from '../../../ui/index.js';
import {
  CLIENT_ANCHOR_ATTR,
  rememberClientsListAnchor,
  type ClientsListReturnState,
} from '../clientsListAnchor.js';
import { TOUCH_TARGET_PX, useActionSize, useTouchInput } from '../useMobileFieldStyle.js';

/** Display form of an E.164 phone; unparseable values are shown verbatim. */
export function displayPhone(phone: string): string {
  return phoneSchema.safeParse(phone).success ? formatPhone(phone) : phone;
}

function stop(e: { stopPropagation: () => void }): void {
  e.stopPropagation();
}

/**
 * Positional wrapper that keeps an in-row control (call link, copy, menu) from
 * also activating the row/card it sits in. Mirrors the guard used by the users
 * registry; the interactive children carry their own roles and key handlers.
 */
export function RowActionGuard({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <span
      onClick={stop}
      onKeyDown={stop}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--s-1)',
        flex: 'none',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export interface ClientNameLinkProps {
  client: ClientListItem;
  /** Filter signature the return anchor is recorded under. */
  filterKey: string;
}

/** Client name as a genuine link, carrying the list's return-anchor hook. */
export function ClientNameLink({ client, filterKey }: ClientNameLinkProps) {
  const attrs = { [CLIENT_ANCHOR_ATTR]: client.id };
  // WHY the state here and not only on the row's own activation handler: this
  // link stops propagation (so a modified click is not ALSO handled by the
  // row), which means entering the detail through the NAME never runs the row
  // path. Without the same return state, detail Back falls back to a bare
  // `/clients` and drops the search, archive scope and manager filter (AC-3).
  // `href` and native modifiers are untouched.
  const returnState: ClientsListReturnState = { clientsListSearch: useLocation().search };
  return (
    <Link
      to={`/clients/${client.id}`}
      state={returnState}
      onClick={(e) => {
        stop(e);
        rememberClientsListAnchor({ filterKey, clientId: client.id });
      }}
      onKeyDown={(e) => {
        // The row and the mobile card activate on Enter/Space and call
        // `preventDefault()`. From inside a real link that breaks the browser's
        // own behaviour twice over: Enter is the LINK's activation (so
        // Ctrl/Cmd+Enter must still be able to open a new tab, and the anchor
        // must still be recorded by its own click handler), and Space is not an
        // activation at all — it scrolls. Keep both out of the row handler.
        if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
      }}
      {...attrs}
      style={{
        color: 'var(--fg-1)',
        fontWeight: 500,
        textDecoration: 'none',
        overflowWrap: 'anywhere',
      }}
    >
      {client.name}
    </Link>
  );
}

/** Name + archived marker. The marker is the only status the list DTO carries. */
export function ClientNameCell({ client, filterKey }: ClientNameLinkProps) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s-2)', minWidth: 0 }}>
      <ClientNameLink client={client} filterKey={filterKey} />
      {client.archivedAt && <StatusBadge status="archived" size="sm" />}
    </span>
  );
}

/** Primary contact name over its formatted phone; em dash when there is none. */
export function ClientContactCell({ client }: { client: ClientListItem }) {
  const { t } = useTranslation(['clients']);
  const contact = client.primaryContact;
  if (!contact)
    return <span style={{ color: 'var(--fg-4)' }}>{t('clients:noPrimaryContact')}</span>;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span style={{ overflowWrap: 'anywhere' }}>{contact.fullName}</span>
      <span
        style={{
          fontSize: 'var(--fz-12)',
          color: 'var(--fg-3)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {displayPhone(contact.phone)}
      </span>
    </span>
  );
}

export interface ClientQuickActionsProps {
  client: ClientListItem;
  onCopyPhone: (client: ClientListItem) => void;
  /** Touch layout: render a 44 px labelled call target instead of the icon rail. */
  touch?: boolean;
}

/**
 * Call + copy for the row's primary contact. Both target THIS row's contact
 * record; there is no ambient "selected contact" that could drift.
 */
export function ClientQuickActions({ client, onCopyPhone, touch }: ClientQuickActionsProps) {
  const { t } = useTranslation(['clients']);
  const actionSize = useActionSize();
  const touchInput = useTouchInput();
  const contact = client.primaryContact;
  if (!contact) return null;
  const callLabel = t('clients:actions.callContact', { name: contact.fullName });
  return (
    <RowActionGuard>
      {touch || touchInput ? (
        <TelLink href={`tel:${contact.phone}`} label={callLabel} />
      ) : (
        <ContactActions
          actions={[{ icon: 'phone', href: `tel:${contact.phone}`, label: callLabel }]}
        />
      )}
      <IconButton
        icon="copy"
        size={actionSize}
        label={t('clients:actions.copyPhoneOf', { name: contact.fullName })}
        onClick={() => onCopyPhone(client)}
      />
    </RowActionGuard>
  );
}

/**
 * 44 px `tel:` target for touch layouts.
 *
 * WHY not `ui/ContactActions`: that primitive's items are a fixed 24 × 24 icon
 * rail, which is the right density for a desktop table row but below the touch
 * target this issue requires on mobile cards (AC-12). The scheme is a literal
 * `tel:` built from the record's own E.164 value, so no href allowlist decision
 * is being re-implemented here.
 */
export function TelLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      title={label}
      aria-label={label}
      style={{
        minWidth: TOUCH_TARGET_PX,
        minHeight: TOUCH_TARGET_PX,
        borderRadius: 'var(--r-2)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--fg-2)',
        textDecoration: 'none',
      }}
    >
      <Icon name="phone" size={18} />
    </a>
  );
}
